<template>
  <div
    v-if="isLinkable"
    class="relative rounded-1"
    :class="{ 'py-3 px-2': !noPadding, 'border-2 border-dashed border-primary3': !noBorder }"
  >
    <slot />

    <div class="absolute top-2 right-2 flex items-center gap-1 z-10">
      <div class="flex items-center gap-1 bg-primary5 rounded px-2 py-1 cursor-pointer" @click.stop="onClick">
        <CIcon icon="link" size="14" />
        <span class="text-xs">{{
          hasLink
            ? t('canvasUI.canvasBuilder.linkableComponent.linked')
            : t('canvasUI.canvasBuilder.linkableComponent.linkable')
        }}</span>
      </div>

      <div
        v-if="hasLink"
        class="flex items-center gap-1 justify-center bg-primary5 color-error rounded p-1 cursor-pointer"
        @click.stop="onUnlink"
      >
        <CIcon icon="unlink" size="14" />
        <span class="text-xs">{{ t('canvasUI.canvasBuilder.linkableComponent.unlink') }}</span>
      </div>
    </div>

    <CModal v-if="showLinkModal" :bordered="false" class="cb-linkable-settings" show size="huge">
      <CCard class="pa-4">
        <div class="text-lg font-bold mb-3">{{ t('canvasUI.canvasBuilder.linkableComponent.linkTo') }}</div>
        <div class="flex flex-col h-[80vh]">
          <CSearch v-model="searchTerm" class="mb-3" />

          <div class="flex-1 overflow-hidden">
            <DynamicScroller
              ref="dynamicScrollerRef"
              class="h-full"
              :items="filteredNavigationItems"
              key-field="uniqueId"
              :min-item-size="60"
            >
              <template #default="{ item: n, active }">
                <DynamicScrollerItem :active="active" :item="n">
                  <div
                    v-if="n.name"
                    class="linkable-item"
                    :class="{ 'linkable-item--active': selectedItem?.uniqueId === n.uniqueId }"
                    @click="selectItem(n)"
                  >
                    <div class="flex items-center gap-3 justify-between">
                      <div class="flex items-center gap-3">
                        <CImage
                          v-if="n.type === 'Section' && mode === 'thumbnails'"
                          class="flex-shrink-0 rounded-2"
                          :height="40"
                          :img-props="{ class: 'w-[65px] h-[40px] object-cover rounded-2' }"
                          loading="lazy"
                          object-fit="cover"
                          :src="n?.thumbnailUrl"
                          :width="65"
                          @click.stop
                        />
                        <span class="font-bold whitespace-pre-wrap">{{ n.name }}</span>
                      </div>
                      <CIcon
                        v-if="isCurrentlyLinked(n)"
                        class="color-primary"
                        fa-type="fas"
                        icon="circle-check"
                        :size="18"
                      />
                    </div>
                  </div>
                </DynamicScrollerItem>
              </template>
            </DynamicScroller>
          </div>

          <div class="flex justify-end gap-2 mt-4 flex-shrink-0">
            <CButton :disabled="isSaving" @click="showLinkModal = false">
              {{ t('canvasUI.common.cancel') }}
            </CButton>
            <CButton :disabled="!selectedItem || isSaving" :loading="isSaving" type="primary" @click="onSave">
              {{ t('canvasUI.common.done') }}
            </CButton>
          </div>
        </div>
      </CCard>
    </CModal>
  </div>
  <div v-else-if="hasLink" class="linkable-component-link cursor-pointer" @click.stop="onNavigate">
    <slot />
  </div>
  <slot v-else />
</template>

<script setup lang="ts">
import { computed, ref, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CImage from '@lib/components/CImage/CImage.vue'
import CModal from '@lib/components/CModal/CModal.vue'
import CCard from '@lib/components/CCard/CCard.vue'
import CSearch from '@lib/components/CSearch/CSearch.vue'
import { type ComponentNode } from '@canvas-builder/types/canvas'
import useThemeVars from '@lib/composables/useThemeVars'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasPages from '@canvas-builder/composables/useCanvasPages'
import useSectionNavigation from '@canvas-builder/composables/useSectionNavigation'
import useCollapsibles from '@canvas-builder/composables/useCollapsibles'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'

const props = withDefaults(
  defineProps<{
    id: ComponentNode['id']
    usedInSectionId?: string
    noPadding?: boolean
    noBorder?: boolean
    mode?: 'thumbnails' | 'list'
  }>(),
  {
    mode: 'thumbnails',
    noPadding: false,
    noBorder: false,
  },
)
const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)
const { t } = useI18n()
const themeVars = useThemeVars()
const {
  isCanvasTemplate,
  isEditMode,
  componentNodesById,
  sectionComponentNodesBySectionId,
  navigationItems,
  activeNavigationItems,
  contentRef,
  saveCanvasContext,
  activeCanvas,
  activateNavigationItem,
} = useCanvas()
const { navigateTo, activeIndex } = useSectionNavigation(contentRef, navigationItems, activeNavigationItems, isEditMode)
const { goToPage } = useCanvasPages()
const collapsiblesStore = useCollapsibles()

const showLinkModal = ref(false)
const selectedItem = ref<(typeof navigationItems.value)[number] | null>(null)
const isSaving = ref(false)
const searchTerm = ref('')

const node = computed(() => {
  if (props.usedInSectionId) {
    return sectionComponentNodesBySectionId.value[props.usedInSectionId]?.[props.id]
  }
  return componentNodesById.value[props.id]
})

const isLinkable = computed(() => {
  if (!isEditMode.value) return false

  if (pitcherSettings.value.canvas?.use_linkable_in_catalogiq) {
    // workaround for catalogiq
    return node.value?.linkable?.linkable_in_templates
  }

  if (!isCanvasTemplate.value) return false

  return node.value?.linkable?.linkable_in_templates
})

const hasLink = computed(() => {
  if (!activeCanvas.value?.context?.pitcher?.component_links_data) return false

  if (props.usedInSectionId) {
    return !!activeCanvas.value.context.pitcher.component_links_data.sections?.[props.usedInSectionId]?.[props.id]
  }
  return !!activeCanvas.value.context.pitcher.component_links_data.components?.[props.id]
})

const filteredNavigationItems = computed(() => {
  let items

  if (!searchTerm.value.trim()) {
    items = navigationItems.value.filter((i) => !!i.name)
  } else {
    const search = searchTerm.value.toLowerCase().trim()
    items = navigationItems.value.filter((item) => !!item.name && item.name?.toLowerCase().includes(search))
  }

  return items
})

const isCurrentlyLinked = (item: (typeof navigationItems.value)[number]) => {
  const currentLink = props.usedInSectionId
    ? activeCanvas.value?.context?.pitcher?.component_links_data?.sections?.[props.usedInSectionId]?.[props.id]
    : activeCanvas.value?.context?.pitcher?.component_links_data?.components?.[props.id]

  if (!currentLink) return false

  const idsMatch = currentLink.id === item.id && currentLink.sectionId === item.sectionId

  // If we have a sectionIndex stored, also check it matches
  if (item.sectionIndex !== undefined && currentLink.sectionIndex !== undefined) {
    return idsMatch && currentLink.sectionIndex === item.sectionIndex
  }

  return idsMatch
}

function onClick() {
  if (hasLink.value) {
    const currentLink = props.usedInSectionId
      ? activeCanvas.value?.context?.pitcher?.component_links_data?.sections?.[props.usedInSectionId]?.[props.id]
      : activeCanvas.value?.context?.pitcher?.component_links_data?.components?.[props.id]

    if (currentLink) {
      // If we have a sectionIndex, match by it too for precise selection
      if (currentLink.sectionIndex !== undefined) {
        selectedItem.value =
          navigationItems.value.find(
            (item) =>
              item.id === currentLink.id &&
              item.sectionId === currentLink.sectionId &&
              item.sectionIndex === currentLink.sectionIndex,
          ) ?? null
      } else {
        selectedItem.value =
          navigationItems.value.find(
            (item) => item.id === currentLink.id && item.sectionId === currentLink.sectionId,
          ) ?? null
      }
    }
  }
  showLinkModal.value = true
  searchTerm.value = ''
}

function selectItem(item: (typeof navigationItems.value)[number]) {
  if (isSaving.value) return
  selectedItem.value = item
}

async function onSave() {
  if (!selectedItem.value || isSaving.value) return

  isSaving.value = true
  try {
    const selection: {
      id: string
      sectionId?: string
      sectionIndex?: number
    } = {
      id: selectedItem.value.id,
      sectionId: selectedItem.value.sectionId,
    }

    // Store sectionIndex if available for precise navigation to duplicate sections
    if (selectedItem.value.sectionIndex !== undefined) {
      selection.sectionIndex = selectedItem.value.sectionIndex
    }

    await saveCanvasContext({
      ...activeCanvas.value?.context,
      pitcher: {
        ...(activeCanvas.value?.context?.pitcher ?? {}),
        component_links_data: {
          components: {
            ...(activeCanvas.value?.context?.pitcher?.component_links_data?.components ?? {}),
            ...(!props.usedInSectionId && {
              [props.id]: selection,
            }),
          },
          sections: {
            ...(activeCanvas.value?.context?.pitcher?.component_links_data?.sections ?? {}),
            ...(props.usedInSectionId && {
              [props.usedInSectionId]: {
                ...(activeCanvas.value?.context?.pitcher?.component_links_data?.sections?.[props.usedInSectionId] ??
                  {}),
                [props.id]: selection,
              },
            }),
          },
        },
      },
    })
    showLinkModal.value = false
    selectedItem.value = null
  } finally {
    isSaving.value = false
  }
}

function onNavigate() {
  if (isEditMode.value || !hasLink.value) return

  const n = props.usedInSectionId
    ? activeCanvas.value?.context?.pitcher?.component_links_data?.sections?.[props.usedInSectionId]?.[props.id]
    : activeCanvas.value?.context?.pitcher?.component_links_data?.components?.[props.id]

  if (!n) return

  const isCollapsiblesMode = (pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') === 'collapsibles'
  const index = navigationItems.value.findIndex((item) => item.id === n.id && item.sectionId === n.sectionId)

  if (index !== -1) activeIndex.value = index

  let itemIndex: number | undefined = undefined
  let targetSectionIndex: number | undefined = undefined

  // Only calculate occurrence if there are duplicate sections and we need to be precise
  if (n.sectionId && props.usedInSectionId) {
    // Find all navigation items with the same target sectionId
    const targetSectionsWithSameId = navigationItems.value.filter(
      (item) => item.sectionId === n.sectionId && item.id === n.id,
    )

    // For single occurrence, just get the sectionIndex
    if (targetSectionsWithSameId.length === 1) {
      targetSectionIndex = targetSectionsWithSameId[0].sectionIndex
    }
    // Only do complex logic if there are multiple occurrences
    else if (targetSectionsWithSameId.length > 1) {
      // Get the section list to understand the pattern
      const sectionListNode = componentNodesById.value[n.id]
      if (
        sectionListNode?.type === 'SectionList' &&
        sectionListNode.data &&
        'sections' in sectionListNode.data &&
        sectionListNode.data.sections
      ) {
        const sections = sectionListNode.data.sections

        // Detect the repeating pattern length by finding the shortest cycle
        let patternLength = 1
        for (let len = 1; len <= sections.length / 2; len++) {
          let isPattern = true
          for (let i = len; i < sections.length; i++) {
            if (sections[i].id !== sections[i % len].id) {
              isPattern = false
              break
            }
          }
          if (isPattern) {
            patternLength = len
            break
          }
        }

        // If no pattern found, treat all sections as one group
        if (patternLength === 1 && sections.length > 1) {
          // Check if there's no repeating pattern at all
          let hasRepeat = false
          for (let len = 1; len <= sections.length / 2; len++) {
            let matches = true
            for (let i = len; i < Math.min(sections.length, len * 2); i++) {
              if (sections[i].id !== sections[i - len].id) {
                matches = false
                break
              }
            }
            if (matches) {
              hasRepeat = true
              break
            }
          }
          if (!hasRepeat) {
            patternLength = sections.length
          }
        }

        // Create groups based on the detected pattern length
        const groups: number[][] = []
        for (let i = 0; i < sections.length; i += patternLength) {
          const group: number[] = []
          for (let j = 0; j < patternLength && i + j < sections.length; j++) {
            group.push(i + j)
          }
          groups.push(group)
        }

        // Find which specific occurrence of the source section is currently active (before clearing)
        const activeItem = activeNavigationItems.value.find(
          (item) => item.id === n.id && item.sectionId === props.usedInSectionId,
        )

        // Use the active item's index to find the correct occurrence
        let sourceSectionIndex = -1
        if (activeItem && activeItem.index !== undefined) {
          sourceSectionIndex = activeItem.index
        } else {
          // Fallback: find first occurrence
          sourceSectionIndex = sections.findIndex((s: { id: string }) => s.id === props.usedInSectionId)
        }

        let sourceGroupIndex = -1

        if (sourceSectionIndex !== -1) {
          for (let i = 0; i < groups.length; i++) {
            if (groups[i].includes(sourceSectionIndex)) {
              sourceGroupIndex = i
              break
            }
          }
        }

        // If we found the source group, find the target section in the same group
        if (sourceGroupIndex !== -1 && groups[sourceGroupIndex]) {
          const targetInGroup = groups[sourceGroupIndex].find((idx: number) => sections[idx]?.id === n.sectionId)

          if (targetInGroup !== undefined) {
            targetSectionIndex = targetInGroup
            // Find which occurrence this is among all targets with same ID
            const occurrenceIndex = targetSectionsWithSameId.findIndex((item) => item.sectionIndex === targetInGroup)
            if (occurrenceIndex !== -1) {
              itemIndex = occurrenceIndex
            }
          }
        }

        // Fallback: use stored sectionIndex if available
        if (itemIndex === undefined && n.sectionIndex !== undefined) {
          itemIndex = targetSectionsWithSameId.findIndex((item) => item.sectionIndex === n.sectionIndex)
          if (itemIndex !== -1 && targetSectionsWithSameId[itemIndex]) {
            targetSectionIndex = targetSectionsWithSameId[itemIndex].sectionIndex
          }
        }

        // Final fallback: first occurrence
        if (itemIndex === undefined || itemIndex === -1) {
          itemIndex = 0
          if (targetSectionsWithSameId[0]) {
            targetSectionIndex = targetSectionsWithSameId[0].sectionIndex
          }
        }
      }
    }
  }

  // Clear previous active items and activate the target to ensure proper highlighting
  // Only clear if we can successfully activate a new item
  if (n.sectionId !== undefined && targetSectionIndex !== undefined) {
    activeNavigationItems.value = []
    activateNavigationItem(n.id, n.sectionId, true, targetSectionIndex)
  }

  if (isCollapsiblesMode) {
    goToPage({ componentId: n.id, sectionId: n.sectionId, itemIndex })
    setTimeout(
      () => {
        if (index !== -1) navigateTo(index)
      },
      n.sectionId ? 250 : 0,
    )
    n.id && n.sectionId && collapsiblesStore.setClickedOnSection(n.id, n.sectionId)
  } else {
    goToPage({ componentId: n.id, scrollTo: 'top', sectionId: n.sectionId, itemIndex })
  }
}

async function onUnlink() {
  await saveCanvasContext({
    ...activeCanvas.value?.context,
    pitcher: {
      ...(activeCanvas.value?.context?.pitcher ?? {}),
      component_links_data: {
        components: {
          ...(activeCanvas.value?.context?.pitcher?.component_links_data?.components ?? {}),
          ...(!props.usedInSectionId && {
            [props.id]: undefined,
          }),
        },
        sections: {
          ...(activeCanvas.value?.context?.pitcher?.component_links_data?.sections ?? {}),
          ...(props.usedInSectionId && {
            [props.usedInSectionId]: {
              ...(activeCanvas.value?.context?.pitcher?.component_links_data?.sections?.[props.usedInSectionId] ?? {}),
              [props.id]: undefined,
            },
          }),
        },
      },
    },
  })
}
</script>

<style lang="scss" scoped>
.cb-linkable-settings {
  width: 80vw;
}
.linkable-item {
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border: 2px solid transparent;
  border-radius: 0.5rem;
  background-color: v-bind('themeVars.base');
  cursor: pointer;
  transition: all 0.2s ease;

  &--active {
    background-color: v-bind('themeVars.primary4');
    border-color: v-bind('themeVars.primary');
  }

  &:hover:not(.linkable-item--active) {
    background-color: v-bind('themeVars.primary5');
  }
}
</style>
